/**
 * Build step 2 — tasks, Work list, Today per role, proof of closure (ADR-001 §11.4, as amended by the
 * operator decisions in docs/architecture/adr-001-decisions.md: #12 proof checking, #18 acting-as).
 *
 * Visibility for every task route: managers see all; an ops_member sees tasks assigned to them and tasks
 * for clients they are the current SPOC of; requesters and HR see none. Out of scope → 404.
 * Every write is audited by the database trigger with the real actor and the acting-as user.
 */
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { BlockTaskBody, ChangeRequest, ChangeRequestIdParams, ChangeRequestPage, CheckClosureBody, CloseTaskBody, ClosureTaskParams, CreateTaskBody, DecideChangeRequestBody, DecideChangeRequestResponse, DropResponse, DropTaskBody, DroppedIdParams, DroppedPage, ListChangeRequestsQuery, ListDroppedQuery, ListTasksQuery, MarkDroppedSeenBody, MoveDueBody, RaiseChangeRequestBody, ReassignTaskBody, ReopenTaskBody, RestoreDroppedBody, Task, TaskEnvelope, TaskIdParams, TaskList, TaskPage, UpdateTaskBody, } from './tasks.js';
import { ListReviewQuery, ListWorkQuery, ReviewPage, WhoseWork, WhoseWorkQuery, WorkPage } from './work.js';
import { ConfirmDayPlanBody, DayPlan, DayPlanQuery, DayStatus, DayStatusBody, MeTodayResponse, SaveDayPlanBody, TodayResponse, } from './today.js';
import { API_BASE, defineRoute } from './route.js';
const TASK_READ = ['tasks.read.all', 'tasks.read.own'];
const SCOPE = 'Managers; or the assignee or the client’s current SPOC. Anyone else: 404.';
const ACT_HEADERS = IdempotencyHeaders.extend(OptionalIfMatchHeaders.shape);
const ETAG = { ETag: 'W/"<version>" of the task' };
const ACTION = { idempotency: 'key', concurrency: 'if-match-optional', rateLimit: 'write' };
export const step2Routes = {
    // ---------------------------------------------------------------- Work
    'work.list': defineRoute({
        id: 'work.list',
        method: 'GET',
        path: `${API_BASE}/work`,
        summary: 'Everything open, in one list (Work → All work)',
        description: 'A discriminated union on `kind`. Step 2 returns task and approval (change request) items; other kinds ' +
            'appear as their build steps land. Overdue first, then due date, then priority.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers → everyone’s; ops_member → own. Others: 403.' },
        query: ListWorkQuery,
        success: { status: 200, description: 'A page of work items', schema: WorkPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'work.whose': defineRoute({
        id: 'work.whose',
        method: 'GET',
        path: `${API_BASE}/work/whose`,
        summary: 'The “Whose work” pill row: Everyone, each person (open, overdue), Not assigned',
        access: { auth: 'session', anyOf: ['tasks.read.all'], note: 'Managers. Others: 403.' },
        query: WhoseWorkQuery,
        success: { status: 200, description: 'Counts per person', schema: WhoseWork },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ---------------------------------------------------------------- tasks
    'tasks.list': defineRoute({
        id: 'tasks.list',
        method: 'GET',
        path: `${API_BASE}/tasks`,
        summary: 'Tasks (filters as GET /work)',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers → all; ops_member → own and own clients’. Others: 403.' },
        query: ListTasksQuery,
        success: { status: 200, description: 'A page of tasks', schema: TaskPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'tasks.get': defineRoute({
        id: 'tasks.get',
        method: 'GET',
        path: `${API_BASE}/tasks/{id}`,
        summary: 'One task with slips, block, closure and change request',
        access: { auth: 'session', anyOf: TASK_READ, note: SCOPE },
        params: TaskIdParams,
        success: { status: 200, description: 'The task', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'tasks.create': defineRoute({
        id: 'tasks.create',
        method: 'POST',
        path: `${API_BASE}/tasks`,
        summary: 'Add a task — or one per ticked date',
        description: 'Only the founder and the associate director (task.reassign) pick the assignee; everyone else gets the default (the ' +
            'client’s SPOC, or themselves for internal work), and a different assignee_id is 403 “Ask for a change ' +
            'instead”. A member may add tasks only for their own clients or internal work (another client → 404).',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers and ops members. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateTaskBody,
        success: { status: 201, description: 'The created task(s)', schema: TaskList },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'tasks.update': defineRoute({
        id: 'tasks.update',
        method: 'PATCH',
        path: `${API_BASE}/tasks/{id}`,
        summary: 'Edit title or priority; managers also set Do first/next/later and manual order',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. head_priority/manual_order: managers only (403).' },
        params: TaskIdParams,
        headers: IfMatchHeaders,
        body: UpdateTaskBody,
        success: { status: 200, description: 'Updated', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'tasks.moveDue': defineRoute({
        id: 'tasks.moveDue',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/due`,
        summary: 'Move the due date, with a reason (later = a slip; 3 slips = flagged)',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. The SPOC alone: 403. Others: 404.' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: MoveDueBody,
        success: { status: 200, description: 'The task, with slip_count and flagged', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'tasks.block': defineRoute({
        id: 'tasks.block',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/block`,
        summary: 'Mark blocked on a person or party',
        description: '409 when already blocked or closed.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. Others: 404 (SPOC alone: 403).' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: BlockTaskBody,
        success: { status: 200, description: 'The task', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'tasks.unblock': defineRoute({
        id: 'tasks.unblock',
        method: 'DELETE',
        path: `${API_BASE}/tasks/{id}/block`,
        summary: 'Clear the blocker',
        description: 'Idempotent: clearing a task that is not blocked returns it unchanged.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. Others: 404 (SPOC alone: 403).' },
        params: TaskIdParams,
        headers: OptionalIfMatchHeaders,
        success: { status: 200, description: 'The task', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    'tasks.reassign': defineRoute({
        id: 'tasks.reassign',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/assignee`,
        summary: 'Give the task to someone else, with a reason',
        description: 'task.reassign only (the founder and the associate director — operator decision). Members get 403 “Ask for ' +
            'a change instead”. The new assignee must be an active ' +
            'founder/ops_ad/ops_member (422). Writes an assignment-log entry.',
        access: { auth: 'session', anyOf: TASK_READ, action: ['task.reassign'], note: 'Founder and Ops associate director. Others who can see the task: 403; else 404.' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: ReassignTaskBody,
        success: { status: 200, description: 'The task', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'tasks.close': defineRoute({
        id: 'tasks.close',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/close`,
        summary: 'Close with proof (a note or a link)',
        description: '422 without proof; MIS tasks also need next_step. The closure starts “not checked” and waits in the ' +
            'Review queue. Marks a linked open client task kept and writes an activity-log row. An engagement-calendar task ' +
            'also logs its cell done today for whoever closes it (a covering assignee too); 409 if that log cannot be written. ' +
            '409 if already closed.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. Others: 404 (SPOC alone: 403).' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: CloseTaskBody,
        success: { status: 200, description: 'The task, closure.check_state = not_checked', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'tasks.reopen': defineRoute({
        id: 'tasks.reopen',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/reopen`,
        summary: 'Reopen a closed task, with a reason',
        description: '409 when the task is not closed.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. Others: 404 (SPOC alone: 403).' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: ReopenTaskBody,
        success: { status: 200, description: 'The task', schema: Task, headers: ETAG },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'tasks.drop': defineRoute({
        id: 'tasks.drop',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/drop`,
        summary: 'Leave a task out on purpose, with a reason',
        description: 'Removes it from the open list and writes a dropped item (Team → Dropped tasks; unseen ones are a manager ' +
            'exception). A member may drop only tasks assigned to them. 409 when closed or already dropped.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Managers, or the assignee. Others: 404 (SPOC alone: 403).' },
        params: TaskIdParams,
        headers: ACT_HEADERS,
        body: DropTaskBody,
        success: { status: 200, description: 'The dropped item', schema: DropResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    // ---------------------------------------------------------------- dropped
    'dropped.list': defineRoute({
        id: 'dropped.list',
        method: 'GET',
        path: `${API_BASE}/dropped`,
        summary: 'Dropped tasks — left out on purpose, with reason (Team tab)',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 403.' },
        query: ListDroppedQuery,
        success: { status: 200, description: 'A page of dropped items, newest first', schema: DroppedPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'dropped.restore': defineRoute({
        id: 'dropped.restore',
        method: 'POST',
        path: `${API_BASE}/dropped/{id}/restore`,
        summary: 'Restore a dropped item as a new task',
        description: '409 when already restored. The new task’s origin_ref points at the dropped item.',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 404.' },
        params: DroppedIdParams,
        headers: IdempotencyHeaders,
        body: RestoreDroppedBody,
        success: { status: 201, description: 'The new task', schema: Task, headers: { Location: '/api/v1/tasks/{id}', ...ETAG } },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'dropped.markSeen': defineRoute({
        id: 'dropped.markSeen',
        method: 'POST',
        path: `${API_BASE}/dropped/seen`,
        summary: 'Mark dropped items seen (clears the manager exception)',
        description: 'Idempotent; unknown ids are ignored.',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 403.' },
        body: MarkDroppedSeenBody,
        success: { status: 204, description: 'Marked' },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- change requests
    'changeRequests.list': defineRoute({
        id: 'changeRequests.list',
        method: 'GET',
        path: `${API_BASE}/change-requests`,
        summary: 'Change requests (managers: all; members: their own)',
        access: {
            auth: 'session',
            anyOf: ['changeRequest.view', 'changeRequest.raise'],
            note: 'Managers → all; ops_member → own. Others: 403.',
        },
        query: ListChangeRequestsQuery,
        success: { status: 200, description: 'A page of change requests, newest first', schema: ChangeRequestPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'changeRequests.raise': defineRoute({
        id: 'changeRequests.raise',
        method: 'POST',
        path: `${API_BASE}/tasks/{id}/change-request`,
        summary: 'Ask to move or drop a task (the task stays with you until decided)',
        description: 'Only the assignee, and only an ops_member (changeRequest.raise). 409 if one is already waiting.',
        access: { auth: 'session', anyOf: TASK_READ, action: ['changeRequest.raise'], note: 'The assignee (ops_member). Others who can see it: 403; else 404.' },
        params: TaskIdParams,
        headers: IdempotencyHeaders,
        body: RaiseChangeRequestBody,
        success: { status: 201, description: 'The change request', schema: ChangeRequest },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'changeRequests.decide': defineRoute({
        id: 'changeRequests.decide',
        method: 'POST',
        path: `${API_BASE}/change-requests/{id}/decide`,
        summary: 'Decide: give to someone else, remove, or keep with the member',
        description: 'changeRequest.decide only (the associate director; the founder views). 409 when already decided. ' +
            'reassign needs user_id; keep needs note; remove writes a dropped item.',
        access: { auth: 'session', anyOf: ['changeRequest.view', 'changeRequest.raise'], action: ['changeRequest.decide'], note: 'Ops associate director. Founder: 403. Others: 404.' },
        params: ChangeRequestIdParams,
        headers: IdempotencyHeaders,
        body: DecideChangeRequestBody,
        success: { status: 200, description: 'The decided request and the task', schema: DecideChangeRequestResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- review + proof
    'reviewQueue.list': defineRoute({
        id: 'reviewQueue.list',
        method: 'GET',
        path: `${API_BASE}/review-queue`,
        summary: 'Team → Review queue: proof checks, change requests, held items, proposals, incidents',
        description: 'Oldest first. Step 2 fills proof and change; the other kinds appear with their steps.',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 403.' },
        query: ListReviewQuery,
        success: { status: 200, description: 'A page of review items', schema: ReviewPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'closures.check': defineRoute({
        id: 'closures.check',
        method: 'POST',
        path: `${API_BASE}/closures/{task_id}/check`,
        summary: 'Check proof: “Looks right” or “Send back”',
        description: 'Operator decision 12: the closer never checks their own proof (403), compared on the REAL person even ' +
            'when acting as someone. The associate director checks (proof.check); a closure by the associate ' +
            'director is checked by the founder (proof.check.ad_closures). send_back reopens the task and creates a ' +
            'closure incident. 409 when not waiting for a check.',
        access: {
            auth: 'session',
            anyOf: TASK_READ,
            action: ['proof.check', 'proof.check.ad_closures'],
            note: 'Ops associate director; founder for the AD’s closures. Others who can see it: 403; else 404.',
        },
        params: ClosureTaskParams,
        headers: IdempotencyHeaders,
        body: CheckClosureBody,
        success: { status: 200, description: 'The task after the check', schema: TaskEnvelope },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ---------------------------------------------------------------- Today
    'today.get': defineRoute({
        id: 'today.get',
        method: 'GET',
        path: `${API_BASE}/today`,
        summary: 'Managers’ Today: four tiles and the exceptions, worst first',
        access: { auth: 'session', anyOf: ['today.company'], note: 'Founder and associate director. Others: 403.' },
        success: { status: 200, description: 'Tiles and exceptions', schema: TodayResponse },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.today': defineRoute({
        id: 'me.today',
        method: 'GET',
        path: `${API_BASE}/me/today`,
        summary: 'The member’s Today: tiles, Needs you (max 5), plan, timesheet card, feedback',
        access: { auth: 'session', anyOf: ['today.own'], note: 'ops_member. Others: 403.' },
        success: { status: 200, description: 'The member’s day', schema: MeTodayResponse },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.plan': defineRoute({
        id: 'me.plan',
        method: 'GET',
        path: `${API_BASE}/me/plan`,
        summary: 'A day plan (own; managers may read anyone’s with user_id)',
        description: 'Generated on first read (ADR §7.6): 9:30 start, 15-minute gaps, lunch 13:00–14:00 skipped, 60/45/30-minute ' +
            'slots for Do first / Do next / Later, placed around the owner’s timed Google Calendar meetings. `calendar` ' +
            'carries those meetings (fixed blocks; titles visible to managers too, operator decision 6) and the owner’s ' +
            'calendar connection; not connected means no meetings and the plan is built without them. A member asking ' +
            'for another user_id gets 404.',
        access: { auth: 'session', anyOf: TASK_READ, note: 'Self; managers for anyone. Others: 403/404.' },
        query: DayPlanQuery,
        success: { status: 200, description: 'The plan', schema: DayPlan },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'me.planSave': defineRoute({
        id: 'me.planSave',
        method: 'POST',
        path: `${API_BASE}/me/plan`,
        summary: 'Move or resize slots in your plan (clears the confirmation)',
        description: 'A task_id that is not in your plan is 422 naming it.',
        access: { auth: 'session', anyOf: ['tasks.read.own', 'tasks.read.all'], note: 'Self only.' },
        body: SaveDayPlanBody,
        success: { status: 200, description: 'The plan', schema: DayPlan },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'me.planConfirm': defineRoute({
        id: 'me.planConfirm',
        method: 'POST',
        path: `${API_BASE}/me/plan/confirm`,
        summary: 'Confirm your plan for the day',
        access: { auth: 'session', anyOf: ['tasks.read.own', 'tasks.read.all'], note: 'Self only.' },
        body: ConfirmDayPlanBody,
        success: { status: 200, description: 'The plan, with confirmed_at', schema: DayPlan },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'me.dayStatus': defineRoute({
        id: 'me.dayStatus',
        method: 'POST',
        path: `${API_BASE}/me/day-status`,
        summary: 'Say how a planned task went today: done, part, or not done',
        description: 'Upserts one answer per (task, day). The task must be in your plan for that day (422). done on an ' +
            'unclosed task returns requires_proof = true — closing still needs POST /tasks/{id}/close with proof.',
        access: { auth: 'session', anyOf: ['tasks.read.own', 'tasks.read.all'], note: 'Self only.' },
        body: DayStatusBody,
        success: { status: 200, description: 'The answer', schema: DayStatus },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
};
//# sourceMappingURL=routes.step2.js.map